import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

// Define types inline since react-grid-layout uses export= syntax
interface Layout {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  maxW?: number
  maxH?: number
  static?: boolean
}

interface Layouts {
  [breakpoint: string]: Layout[]
}

export type WidgetType =
  | 'fixtures'
  | 'leagues'
  | 'dixon'
  | 'alerts'
  | 'highlights'
  | 'trending'

export interface WidgetConfig {
  id: string
  type: WidgetType
  enabled: boolean
  settings?: Record<string, unknown>
}

// Default widget configurations
const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'fixtures', type: 'fixtures', enabled: true },
  { id: 'leagues', type: 'leagues', enabled: true },
  { id: 'dixon', type: 'dixon', enabled: true },
  { id: 'alerts', type: 'alerts', enabled: true },
  { id: 'highlights', type: 'highlights', enabled: true },
  { id: 'trending', type: 'trending', enabled: true },
]

// Default layouts for each breakpoint
const DEFAULT_LAYOUTS: Layouts = {
  lg: [
    { i: 'fixtures', x: 0, y: 0, w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'leagues', x: 4, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'dixon', x: 8, y: 0, w: 4, h: 5, minW: 3, minH: 3 },
    { i: 'alerts', x: 4, y: 2, w: 4, h: 3, minW: 2, minH: 2 },
    { i: 'highlights', x: 0, y: 5, w: 6, h: 4, minW: 3, minH: 3 },
    { i: 'trending', x: 6, y: 5, w: 6, h: 4, minW: 3, minH: 3 },
  ],
  md: [
    { i: 'fixtures', x: 0, y: 0, w: 5, h: 5, minW: 3, minH: 3 },
    { i: 'leagues', x: 5, y: 0, w: 5, h: 2, minW: 2, minH: 2 },
    { i: 'dixon', x: 0, y: 5, w: 5, h: 5, minW: 3, minH: 3 },
    { i: 'alerts', x: 5, y: 2, w: 5, h: 3, minW: 2, minH: 2 },
    { i: 'highlights', x: 5, y: 5, w: 5, h: 5, minW: 3, minH: 3 },
    { i: 'trending', x: 0, y: 10, w: 10, h: 3, minW: 3, minH: 3 },
  ],
  sm: [
    { i: 'fixtures', x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3 },
    { i: 'leagues', x: 0, y: 4, w: 6, h: 2, minW: 2, minH: 2 },
    { i: 'dixon', x: 0, y: 6, w: 6, h: 4, minW: 3, minH: 3 },
    { i: 'alerts', x: 0, y: 10, w: 6, h: 3, minW: 2, minH: 2 },
    { i: 'highlights', x: 0, y: 13, w: 6, h: 4, minW: 3, minH: 3 },
    { i: 'trending', x: 0, y: 17, w: 6, h: 3, minW: 3, minH: 3 },
  ],
  xs: [
    { i: 'fixtures', x: 0, y: 0, w: 4, h: 4 },
    { i: 'leagues', x: 0, y: 4, w: 4, h: 2 },
    { i: 'dixon', x: 0, y: 6, w: 4, h: 4 },
    { i: 'alerts', x: 0, y: 10, w: 4, h: 3 },
    { i: 'highlights', x: 0, y: 13, w: 4, h: 4 },
    { i: 'trending', x: 0, y: 17, w: 4, h: 3 },
  ],
  xxs: [
    { i: 'fixtures', x: 0, y: 0, w: 2, h: 4 },
    { i: 'leagues', x: 0, y: 4, w: 2, h: 2 },
    { i: 'dixon', x: 0, y: 6, w: 2, h: 4 },
    { i: 'alerts', x: 0, y: 10, w: 2, h: 3 },
    { i: 'highlights', x: 0, y: 13, w: 2, h: 4 },
    { i: 'trending', x: 0, y: 17, w: 2, h: 3 },
  ],
}

const STORAGE_KEY = 'statscout_dashboard_layout'

interface DashboardState {
  layouts: Layouts
  widgets: WidgetConfig[]
  isEditing: boolean
  isDirty: boolean
}

interface DashboardContextType extends DashboardState {
  setLayouts: (layouts: Layouts) => void
  addWidget: (widgetType: WidgetType) => void
  removeWidget: (widgetId: string) => void
  updateWidgetSettings: (widgetId: string, settings: Record<string, unknown>) => void
  toggleEditMode: () => void
  saveLayout: () => void
  resetToDefault: () => void
}

const DashboardContext = createContext<DashboardContextType | null>(null)

function loadFromStorage(): { layouts: Layouts; widgets: WidgetConfig[] } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed.layouts && parsed.widgets) {
        return parsed
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

function saveToStorage(layouts: Layouts, widgets: WidgetConfig[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ layouts, widgets }))
}

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DashboardState>(() => {
    const stored = loadFromStorage()
    return {
      layouts: stored?.layouts || DEFAULT_LAYOUTS,
      widgets: stored?.widgets || DEFAULT_WIDGETS,
      isEditing: false,
      isDirty: false,
    }
  })

  const setLayouts = useCallback((layouts: Layouts) => {
    setState((prev) => ({
      ...prev,
      layouts,
      isDirty: true,
    }))
  }, [])

  const addWidget = useCallback((widgetType: WidgetType) => {
    setState((prev) => {
      // Check if widget already exists and is enabled
      const existing = prev.widgets.find((w) => w.type === widgetType)
      if (existing?.enabled) return prev

      const newWidgets = existing
        ? prev.widgets.map((w) =>
            w.type === widgetType ? { ...w, enabled: true } : w
          )
        : [...prev.widgets, { id: widgetType, type: widgetType, enabled: true }]

      // Add to layout if needed
      const newLayouts = { ...prev.layouts }
      Object.keys(newLayouts).forEach((breakpoint) => {
        const layout = newLayouts[breakpoint]
        if (!layout.find((l: Layout) => l.i === widgetType)) {
          // Add at the bottom
          const maxY = Math.max(...layout.map((l) => l.y + l.h), 0)
          layout.push({
            i: widgetType,
            x: 0,
            y: maxY,
            w: breakpoint === 'lg' ? 4 : breakpoint === 'md' ? 5 : 6,
            h: 3,
            minW: 2,
            minH: 2,
          })
        }
      })

      return {
        ...prev,
        widgets: newWidgets,
        layouts: newLayouts,
        isDirty: true,
      }
    })
  }, [])

  const removeWidget = useCallback((widgetId: string) => {
    setState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) =>
        w.id === widgetId ? { ...w, enabled: false } : w
      ),
      isDirty: true,
    }))
  }, [])

  const updateWidgetSettings = useCallback(
    (widgetId: string, settings: Record<string, unknown>) => {
      setState((prev) => ({
        ...prev,
        widgets: prev.widgets.map((w) =>
          w.id === widgetId ? { ...w, settings: { ...w.settings, ...settings } } : w
        ),
        isDirty: true,
      }))
    },
    []
  )

  const toggleEditMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
    }))
  }, [])

  const saveLayout = useCallback(() => {
    saveToStorage(state.layouts, state.widgets)
    setState((prev) => ({ ...prev, isDirty: false }))
  }, [state.layouts, state.widgets])

  const resetToDefault = useCallback(() => {
    setState({
      layouts: DEFAULT_LAYOUTS,
      widgets: DEFAULT_WIDGETS,
      isEditing: false,
      isDirty: true,
    })
  }, [])

  // Auto-save when dirty and not editing
  useEffect(() => {
    if (state.isDirty && !state.isEditing) {
      const timeout = setTimeout(() => {
        saveToStorage(state.layouts, state.widgets)
        setState((prev) => ({ ...prev, isDirty: false }))
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [state.isDirty, state.isEditing, state.layouts, state.widgets])

  return (
    <DashboardContext.Provider
      value={{
        ...state,
        setLayouts,
        addWidget,
        removeWidget,
        updateWidgetSettings,
        toggleEditMode,
        saveLayout,
        resetToDefault,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

export function useDashboard() {
  const ctx = useContext(DashboardContext)
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider')
  return ctx
}

export { DEFAULT_LAYOUTS, DEFAULT_WIDGETS }
